import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Lock, KeyRound, Loader2, Building2, UserCog } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/i18n/I18nProvider";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

export default function Login() {
  const { t, lang, setLang, languages } = useI18n();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("credentials");
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [token, setToken] = useState(null);
  const [mfaSetup, setMfaSetup] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryInput, setRecoveryInput] = useState("");
  const [activationAvailable, setActivationAvailable] = useState(false);
  const [actUsername, setActUsername] = useState("");
  const [actPassword, setActPassword] = useState("");
  const [actConfirm, setActConfirm] = useState("");
  const [actSecret, setActSecret] = useState("");
  const [staffUsername, setStaffUsername] = useState("");
  const [staffCode, setStaffCode] = useState("");
  const [staffPassword, setStaffPassword] = useState("");
  const [staffConfirm, setStaffConfirm] = useState("");

  async function submitStaffActivation(e) {
    e.preventDefault();
    if (staffPassword.length < 8) return toast.error("Password must be at least 8 characters");
    if (staffPassword !== staffConfirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/activate", {
        username: staffUsername,
        activation_code: staffCode,
        new_password: staffPassword,
      });
      toast.success("Account activated");
      await handleAuthResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    api.get("/auth/owner-activation-status")
      .then((r) => setActivationAvailable(!!r.data.available))
      .catch(() => setActivationAvailable(true)); // fail-open: still show the button; backend enforces one-time
  }, []);

  async function submitActivation(e) {
    e.preventDefault();
    if (actPassword.length < 8) return toast.error("Password must be at least 8 characters");
    if (actPassword !== actConfirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/activate-owner", {
        username: actUsername,
        new_password: actPassword,
        activation_secret: actSecret,
      });
      toast.success("Owner account activated");
      await handleAuthResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  async function handleAuthResult(data) {
    if (data.status === "success") {
      if (data.recovery_codes) {
        setRecoveryCodes(data.recovery_codes);
        setPendingUser(data.user);
        setStep("recovery_codes");
        return;
      }
      setUser(data.user);
      toast.success(`Welcome, ${data.user.full_name}`);
      navigate("/");
      return;
    }
    setToken(data.token);
    if (data.status === "password_change_required") {
      setStep("change_password");
    } else if (data.status === "mfa_setup_required") {
      const res = await api.post("/auth/mfa/setup-init", { token: data.token });
      setMfaSetup(res.data);
      setStep("mfa_setup");
    } else if (data.status === "mfa_required") {
      setStep("mfa_verify");
    }
  }

  async function submitCredentials(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      await handleAuthResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  }

  async function submitNewPassword(e) {
    e.preventDefault();
    if (newPassword.length < 8) return toast.error("Password must be at least 8 characters");
    if (newPassword !== confirmPassword) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        change_token: token,
        new_password: newPassword,
      });
      toast.success("Password updated");
      await handleAuthResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  async function submitMfa(endpoint) {
    if (code.length !== 6) return toast.error("Enter the 6-digit code");
    setLoading(true);
    try {
      const { data } = await api.post(endpoint, { token, code });
      await handleAuthResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setCode("");
    } finally {
      setLoading(false);
    }
  }

  async function submitRecovery() {
    if (recoveryInput.trim().length < 6) return toast.error("Enter a valid recovery code");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { token, code: recoveryInput.trim() });
      await handleAuthResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  function finishRecoveryCodes() {
    setUser(pendingUser);
    toast.success(`Welcome, ${pendingUser.full_name}`);
    navigate("/");
  }

  return (
    <div className="min-h-screen w-full flex bg-navy">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[44%] p-14 bg-navy text-white relative overflow-hidden">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-gold/10 blur-3xl" />
        <div className="flex items-center gap-3 relative z-10">
          <div className="h-11 w-11 rounded-xl bg-gold flex items-center justify-center">
            <Building2 className="h-6 w-6 text-navy" strokeWidth={2.4} />
          </div>
          <div>
            <p className="font-heading text-lg font-semibold leading-none">Dental Clinic OS</p>
            <p className="text-xs text-white/50 mt-1">Istanbul · Operations Platform</p>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="font-heading text-4xl xl:text-5xl font-semibold leading-tight tracking-tight">
            {t("auth.heroTitle")}
          </h1>
          <p className="text-white/60 mt-5 max-w-md leading-relaxed">
            {t("auth.heroSub")}
          </p>
          <div className="flex items-center gap-2 mt-8 text-gold text-sm">
            <ShieldCheck className="h-4 w-4" />
            <span>{t("auth.secured")}</span>
          </div>
        </div>
        <p className="text-white/30 text-xs relative z-10">Synthetic development data · Not for production</p>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center bg-ivory p-6 py-10 min-h-screen overflow-y-auto">
        <div className="w-full max-w-md">
          <div className="flex justify-end mb-4" data-testid="login-language-switcher">
            <div className="flex items-center gap-1 rounded-full bg-slate-100 p-1">
              {languages.map((l) => (
                <button key={l.code} type="button" data-testid={`login-lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${lang === l.code ? "bg-navy text-white" : "text-slate-500 hover:text-navy"}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-navy flex items-center justify-center">
              <Building2 className="h-5 w-5 text-gold" />
            </div>
            <span className="font-heading font-semibold text-navy text-lg">Dental Clinic OS</span>
          </div>

          {step === "credentials" && (
            <form onSubmit={submitCredentials} className="space-y-5" data-testid="login-form">
              <div>
                <h2 className="font-heading text-2xl font-semibold text-navy">{t("auth.login")}</h2>
                <p className="text-sm text-slate-500 mt-1">{t("auth.useAccount")}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="username">{t("auth.username")}</Label>
                <Input
                  id="username"
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="e.g. coordination1"
                  autoComplete="username"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("auth.passwordPlaceholder")}
                  autoComplete="current-password"
                  required
                />
              </div>
              <button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-navy hover:bg-navy-soft text-white font-medium h-12 text-base border border-navy shadow-sm transition-colors disabled:opacity-70"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.login")}
              </button>

              {activationAvailable ? (
                <div className="pt-3 mt-1 border-t border-slate-200">
                  <button
                    type="button"
                    data-testid="owner-activation-link"
                    onClick={() => setStep("activate")}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-gold bg-gold/10 text-navy font-medium h-11 hover:bg-gold/20 transition-colors"
                  >
                    <UserCog className="h-4 w-4" /> First time here? Activate the Owner account
                  </button>
                  <p className="text-xs text-slate-400 mt-2 text-center">
                    Set your own Owner password — no password has been created yet.
                  </p>
                </div>
              ) : (
                <div className="pt-3 mt-1 border-t border-slate-200">
                  <p className="text-xs text-slate-400 text-center">
                    {t("auth.ownerActivated")}
                  </p>
                </div>
              )}

              <div>
                <button
                  type="button"
                  data-testid="staff-activation-link"
                  onClick={() => setStep("activate_staff")}
                  className="w-full text-sm text-slate-500 hover:text-navy nav-link"
                >
                  {t("auth.firstTimeEmployee")}
                </button>
              </div>
            </form>
          )}

          {step === "activate_staff" && (
            <form onSubmit={submitStaffActivation} className="space-y-5" data-testid="staff-activate-form">
              <div className="flex items-center gap-2 text-navy">
                <UserCog className="h-5 w-5" />
                <h2 className="font-heading text-2xl font-semibold">Activate your account</h2>
              </div>
              <p className="text-sm text-slate-500">
                Enter the one-time activation code from your administrator, then set your own private password.
              </p>
              <div className="space-y-2">
                <Label htmlFor="s-username">Username</Label>
                <Input id="s-username" data-testid="staff-activate-username" value={staffUsername}
                  onChange={(e) => setStaffUsername(e.target.value)} placeholder="e.g. secretary1" autoComplete="username" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="s-code">Activation code</Label>
                <Input id="s-code" data-testid="staff-activate-code" value={staffCode}
                  onChange={(e) => setStaffCode(e.target.value)} placeholder="XXXX-XXXX-XXXX"
                  className="font-mono tracking-widest" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="s-pw">New password</Label>
                <Input id="s-pw" data-testid="staff-activate-password" type="password" value={staffPassword}
                  onChange={(e) => setStaffPassword(e.target.value)} placeholder="Upper, lower, number & symbol" autoComplete="new-password" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="s-confirm">Confirm password</Label>
                <Input id="s-confirm" data-testid="staff-activate-confirm" type="password" value={staffConfirm}
                  onChange={(e) => setStaffConfirm(e.target.value)} autoComplete="new-password" required />
              </div>
              <button type="submit" data-testid="staff-activate-submit" disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-navy hover:bg-navy-soft text-white font-medium h-12 text-base border border-navy shadow-sm transition-colors disabled:opacity-70">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activate & continue"}
              </button>
              <button type="button" onClick={() => setStep("credentials")} className="w-full text-sm text-slate-500 hover:text-navy nav-link">
                Back to sign in
              </button>
            </form>
          )}

          {step === "activate" && (
            <form onSubmit={submitActivation} className="space-y-5" data-testid="activate-form">
              <div className="flex items-center gap-2 text-navy">
                <UserCog className="h-5 w-5" />
                <h2 className="font-heading text-2xl font-semibold">Activate Owner account</h2>
              </div>
              <p className="text-sm text-slate-500">
                Create your private Owner password. Next you'll set up authenticator MFA and get
                one-time recovery codes.
              </p>
              <div className="space-y-2">
                <Label htmlFor="act-username">Owner username</Label>
                <Input
                  id="act-username"
                  data-testid="activate-username-input"
                  value={actUsername}
                  onChange={(e) => setActUsername(e.target.value)}
                  autoComplete="username"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="act-password">New password</Label>
                <Input
                  id="act-password"
                  data-testid="activate-password-input"
                  type="password"
                  value={actPassword}
                  onChange={(e) => setActPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="act-confirm">Confirm password</Label>
                <Input
                  id="act-confirm"
                  data-testid="activate-confirm-input"
                  type="password"
                  value={actConfirm}
                  onChange={(e) => setActConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="act-secret">Owner activation secret</Label>
                <Input
                  id="act-secret"
                  data-testid="activate-secret-input"
                  type="password"
                  value={actSecret}
                  onChange={(e) => setActSecret(e.target.value)}
                  placeholder="One-time claim secret (from OWNER_ACTIVATION_SECRET)"
                  autoComplete="off"
                  required
                />
              </div>
              <button
                type="submit"
                data-testid="activate-submit-button"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-navy hover:bg-navy-soft text-white font-medium h-12 text-base border border-navy shadow-sm transition-colors disabled:opacity-70"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create password & continue"}
              </button>
              <button
                type="button"
                onClick={() => setStep("credentials")}
                className="w-full text-sm text-slate-500 hover:text-navy nav-link"
              >
                Back to sign in
              </button>
            </form>
          )}

          {step === "change_password" && (
            <form onSubmit={submitNewPassword} className="space-y-5" data-testid="change-password-form">
              <div className="flex items-center gap-2 text-navy">
                <KeyRound className="h-5 w-5" />
                <h2 className="font-heading text-2xl font-semibold">Set a new password</h2>
              </div>
              <p className="text-sm text-slate-500">
                This is your first sign-in. Replace the temporary password to continue.
              </p>
              <div className="space-y-2">
                <Label htmlFor="newpw">New password</Label>
                <Input
                  id="newpw"
                  data-testid="new-password-input"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmpw">Confirm password</Label>
                <Input
                  id="confirmpw"
                  data-testid="confirm-password-input"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                data-testid="change-password-submit"
                disabled={loading}
                className="w-full bg-navy hover:bg-navy-soft text-white h-11"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
          )}

          {step === "mfa_setup" && mfaSetup && (
            <div className="space-y-5" data-testid="mfa-setup">
              <div className="flex items-center gap-2 text-navy">
                <ShieldCheck className="h-5 w-5" />
                <h2 className="font-heading text-2xl font-semibold">Set up authenticator</h2>
              </div>
              <p className="text-sm text-slate-500">
                Scan this QR code with Google Authenticator (or any TOTP app), then enter the 6-digit code.
              </p>
              <div className="flex justify-center">
                <img
                  src={mfaSetup.qr_data_url}
                  alt="MFA QR code"
                  className="h-44 w-44 rounded-xl border border-slate-200 bg-white p-2"
                />
              </div>
              <div className="rounded-lg bg-slate-100 p-3 text-center">
                <p className="text-xs text-slate-500">Manual key</p>
                <p className="font-mono text-sm text-navy break-all" data-testid="mfa-secret">
                  {mfaSetup.secret}
                </p>
              </div>
              <div className="flex flex-col items-center gap-3">
                <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="mfa-otp-input">
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="h-11 w-11 text-base" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
                <Button
                  onClick={() => submitMfa("/auth/mfa/setup-verify")}
                  data-testid="mfa-setup-verify-button"
                  disabled={loading}
                  className="w-full bg-navy hover:bg-navy-soft text-white h-11"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enable & sign in"}
                </Button>
              </div>
            </div>
          )}

          {step === "mfa_verify" && (
            <div className="space-y-5" data-testid="mfa-verify">
              <div className="flex items-center gap-2 text-navy">
                <Lock className="h-5 w-5" />
                <h2 className="font-heading text-2xl font-semibold">Two-factor verification</h2>
              </div>
              {!useRecovery ? (
                <>
                  <p className="text-sm text-slate-500">Enter the 6-digit code from your authenticator app.</p>
                  <div className="flex flex-col items-center gap-4">
                    <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="mfa-verify-otp-input">
                      <InputOTPGroup>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <InputOTPSlot key={i} index={i} className="h-11 w-11 text-base" />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                    <Button
                      onClick={() => submitMfa("/auth/mfa/verify")}
                      data-testid="mfa-verify-button"
                      disabled={loading}
                      className="w-full bg-navy hover:bg-navy-soft text-white h-11"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & sign in"}
                    </Button>
                    <button
                      type="button"
                      data-testid="use-recovery-link"
                      onClick={() => setUseRecovery(true)}
                      className="text-sm text-slate-500 hover:text-navy nav-link"
                    >
                      Lost your device? Use a recovery code
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-500">Enter one of the one-time recovery codes you saved during setup.</p>
                  <div className="space-y-3">
                    <Input
                      data-testid="recovery-code-input"
                      value={recoveryInput}
                      onChange={(e) => setRecoveryInput(e.target.value)}
                      placeholder="XXXX-XXXX"
                      className="font-mono tracking-widest text-center"
                    />
                    <Button
                      onClick={submitRecovery}
                      data-testid="recovery-verify-button"
                      disabled={loading}
                      className="w-full bg-navy hover:bg-navy-soft text-white h-11"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in with recovery code"}
                    </Button>
                    <button
                      type="button"
                      onClick={() => setUseRecovery(false)}
                      className="text-sm text-slate-500 hover:text-navy nav-link w-full text-center"
                    >
                      Back to authenticator code
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {step === "recovery_codes" && recoveryCodes && (
            <div className="space-y-5" data-testid="recovery-codes-screen">
              <div className="flex items-center gap-2 text-navy">
                <KeyRound className="h-5 w-5" />
                <h2 className="font-heading text-2xl font-semibold">Save your recovery codes</h2>
              </div>
              <p className="text-sm text-slate-500">
                Store these somewhere safe. Each code works <strong>once</strong> if you lose your
                authenticator. They will not be shown again.
              </p>
              <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-100 p-4">
                {recoveryCodes.map((c) => (
                  <span key={c} className="font-mono text-sm text-navy text-center py-1.5 bg-white rounded border border-slate-200">
                    {c}
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => { navigator.clipboard.writeText(recoveryCodes.join("\n")); toast.success("Copied"); }}
                  data-testid="copy-recovery-codes"
                  className="flex-1 h-11"
                >
                  Copy codes
                </Button>
                <Button
                  onClick={finishRecoveryCodes}
                  data-testid="recovery-continue-button"
                  className="flex-1 bg-navy hover:bg-navy-soft text-white h-11"
                >
                  I saved them, continue
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
